/* PWW Analytics Framework v101
 * Goal: own PWW-specific customer intelligence without duplicating Shopify's built-in ecommerce tracking.
 * Sends events to GA4 when gtag/dataLayer is present, and logs events when ?pww_debug=1 is in the URL.
 */
(function () {
  'use strict';

  var VERSION = 'v101.0';
  var DEBUG_KEY = 'pww_analytics_debug';
  var sent = {};
  var lastCartSnapshot = null;

  function params() { return new URLSearchParams(window.location.search || ''); }
  function debugEnabled() {
    try {
      if (params().get('pww_debug') === '1') localStorage.setItem(DEBUG_KEY, '1');
      if (params().get('pww_debug') === '0') localStorage.removeItem(DEBUG_KEY);
      return localStorage.getItem(DEBUG_KEY) === '1' || params().get('pww_debug') === '1';
    } catch (e) { return params().get('pww_debug') === '1'; }
  }
  function log() {
    if (!debugEnabled() || !window.console) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[PWW Analytics ' + VERSION + ']');
    console.log.apply(console, args);
  }
  function nowIso() { return new Date().toISOString(); }
  function clean(value) {
    if (value === undefined || value === null) return undefined;
    if (typeof value === 'string') return value.trim().slice(0, 500) || undefined;
    return value;
  }
  function compact(obj) {
    var out = {};
    Object.keys(obj || {}).forEach(function (key) {
      var value = obj[key];
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) && !value.length) return;
      out[key] = value;
    });
    return out;
  }
  function moneyToNumber(cents) {
    var n = Number(cents);
    return isFinite(n) ? Math.round(n) / 100 : undefined;
  }
  function dedupeKey(eventName, payload) {
    var base = eventName + '|' + (payload.event_id || '') + '|' + (payload.item_id || '') + '|' + (payload.page_location || location.href);
    return base.slice(0, 250);
  }
  function shouldSend(eventName, payload, ttlMs) {
    ttlMs = ttlMs || 1000;
    var key = dedupeKey(eventName, payload || {});
    var ts = Date.now();
    if (sent[key] && ts - sent[key] < ttlMs) return false;
    sent[key] = ts;
    return true;
  }
  function ga4ItemsFromCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    return cart.items.map(function (item, index) {
      return compact({
        item_id: String(item.sku || item.variant_id || item.product_id || ''),
        item_name: item.product_title || item.title,
        item_brand: item.vendor,
        item_category: item.product_type,
        item_variant: item.variant_title,
        price: moneyToNumber(item.final_price || item.price),
        quantity: item.quantity,
        index: index
      });
    });
  }
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
  }
  function getVehicleContext() {
    var p = params();
    var sessionVehicle = null;
    try { sessionVehicle = JSON.parse(sessionStorage.getItem('pww_last_vehicle_fitment') || 'null'); } catch(e) {}
    return compact({
      pww_vehicle: clean(p.get('pww_vehicle')) || clean(sessionVehicle && (sessionVehicle.label || [sessionVehicle.year, sessionVehicle.make, sessionVehicle.model].filter(Boolean).join(' '))),
      pww_shop: clean(p.get('pww_shop')),
      pww_vehicle_bolt: clean(p.get('pww_vehicle_bolt')),
      pww_wheel_sizes: clean(p.get('pww_wheel_sizes')),
      pww_tire_sizes: clean(p.get('pww_tire_sizes')),
      pww_vehicle_year: clean(sessionVehicle && sessionVehicle.year),
      pww_vehicle_make: clean(sessionVehicle && sessionVehicle.make),
      pww_vehicle_model: clean(sessionVehicle && sessionVehicle.model)
    });
  }
  function basePayload(payload) {
    var cfg = window.PWW_ANALYTICS_CONFIG || {};
    return compact(Object.assign({
      pww_version: VERSION,
      page_title: document.title,
      page_location: location.href,
      page_path: location.pathname,
      page_type: cfg.page_type,
      template: cfg.template,
      currency: cfg.currency || 'CAD',
      event_time: nowIso()
    }, getVehicleContext(), payload || {}));
  }
  function send(eventName, payload, options) {
    options = options || {};
    payload = basePayload(payload);
    if (!shouldSend(eventName, payload, options.dedupe_ms)) {
      log('deduped', eventName, payload);
      return;
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(Object.assign({ event: eventName }, payload));
    if (typeof window.gtag === 'function') {
      try { window.gtag('event', eventName, payload); } catch (e) { log('gtag error', e); }
    }
    log('sent', eventName, payload);
    window.dispatchEvent(new CustomEvent('pww:analytics:event', { detail: { event: eventName, payload: payload }}));
  }
  function trackStandard(eventName, payload) {
    // Standard GA4 ecommerce names; only use where Shopify DebugView shows gaps.
    send(eventName, payload, { dedupe_ms: 1500 });
  }
  function trackCustom(eventName, payload) {
    send('pww_' + eventName, payload, { dedupe_ms: 1000 });
  }
  function productContextFromWindow() {
    var cfg = window.PWW_ANALYTICS_CONFIG || {};
    var product = cfg.product || {};
    return compact(product);
  }
  function itemFromProductContext(quantity) {
    var p = productContextFromWindow();
    return compact({
      item_id: p.sku || p.variant_id || p.product_id,
      item_name: p.title,
      item_brand: p.vendor,
      item_category: p.product_type,
      item_variant: p.variant_title,
      price: p.price,
      quantity: quantity || p.quantity || 1,
      pww_wheel_diameter: p.wheel_diameter,
      pww_wheel_width: p.wheel_width,
      pww_wheel_bolt_pattern: p.wheel_bolt_pattern,
      pww_wheel_offset: p.wheel_offset,
      pww_wheel_bore: p.wheel_bore,
      pww_tire_width: p.tire_width,
      pww_tire_aspect_ratio: p.tire_aspect_ratio,
      pww_tire_rim_diameter: p.tire_rim_diameter,
      pww_supplier: p.supplier
    });
  }
  function itemFromCartAddResponse(response, formData) {
    var quantity = Number((formData && formData.get && formData.get('quantity')) || response.quantity || 1);
    return compact({
      item_id: response.sku || response.variant_id || response.id,
      item_name: response.product_title || response.title || productContextFromWindow().title,
      item_brand: response.vendor || productContextFromWindow().vendor,
      item_category: response.product_type || productContextFromWindow().product_type,
      item_variant: response.variant_title || productContextFromWindow().variant_title,
      price: moneyToNumber(response.final_price || response.price) || productContextFromWindow().price,
      quantity: quantity || 1
    });
  }
  function cardPayload(card) {
    if (!card) return {};
    var link = card.querySelector('a[href*="/products/"]');
    var title = card.getAttribute('data-pww-product-title') || (link && link.textContent) || '';
    return compact({
      item_id: card.getAttribute('data-pww-variant-id') || card.getAttribute('data-pww-product-id'),
      item_name: title.replace(/\s+/g, ' ').trim(),
      item_brand: card.getAttribute('data-pww-product-vendor'),
      item_category: card.getAttribute('data-pww-product-type'),
      price: Number(card.getAttribute('data-pww-product-price')) || undefined,
      item_url: link ? link.href : undefined,
      list_name: (window.PWW_ANALYTICS_CONFIG || {}).collection_title || document.title
    });
  }
  function initCollectionTracking() {
    var cfg = window.PWW_ANALYTICS_CONFIG || {};
    var grid = document.querySelector('#product-grid, .product-grid, [data-pww-featured-carousel]');
    if (!grid) return;
    var cards = Array.prototype.slice.call(document.querySelectorAll('[data-pww-product-card], .product-card-wrapper, .grid__item')).slice(0, 100);
    if (cards.length && (cfg.page_type === 'collection' || cfg.page_type === 'search' || location.pathname.indexOf('/collections/') === 0 || location.pathname.indexOf('/search') === 0)) {
      trackStandard('view_item_list', compact({
        item_list_name: cfg.collection_title || cfg.search_term || document.title,
        item_list_id: cfg.collection_handle || cfg.page_type,
        items_visible: cards.length,
        search_term: cfg.search_term
      }));
    }
    document.addEventListener('click', function (evt) {
      var link = evt.target.closest && evt.target.closest('[data-pww-product-card] a[href*="/products/"], .product-card-wrapper a[href*="/products/"], .grid__item a[href*="/products/"]');
      if (!link) return;
      var card = link.closest('[data-pww-product-card], .product-card-wrapper, .grid__item');
      trackStandard('select_item', compact({
        item_list_name: cfg.collection_title || document.title,
        items: [cardPayload(card)]
      }));
    }, true);
  }
  function initCartTracking() {
    if (location.pathname.replace(/\/$/, '') === '/cart') {
      getCart().then(function (cart) {
        if (!cart) return;
        lastCartSnapshot = cart;
        trackStandard('view_cart', compact({
          currency: cart.currency || (window.PWW_ANALYTICS_CONFIG || {}).currency || 'CAD',
          value: moneyToNumber(cart.total_price),
          items: ga4ItemsFromCart(cart),
          cart_item_count: cart.item_count
        }));
      });
    }
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (data) {
        var response = data && data.cartData;
        if (!response) return;
        var item;
        if (response.items && response.items.length) {
          item = ga4ItemsFromCart({ items: response.items }).slice(-1)[0];
        } else {
          item = itemFromProductContext();
        }
        trackStandard('add_to_cart', compact({
          currency: (window.PWW_ANALYTICS_CONFIG || {}).currency || 'CAD',
          value: item && item.price ? item.price * (item.quantity || 1) : undefined,
          items: item ? [item] : undefined,
          productVariantId: data.productVariantId,
          source: data.source || 'product-form'
        }));
      });
    }

    window.addEventListener('pww:add_to_cart_success', function (evt) {
      var d = evt.detail || {};
      var item = itemFromCartAddResponse(d.cartData || {}, { get: function (name) { return name === 'quantity' ? d.quantity : (name === 'id' ? d.productVariantId : undefined); } });
      trackStandard('add_to_cart', compact({
        currency: (window.PWW_ANALYTICS_CONFIG || {}).currency || 'CAD',
        value: item && item.price ? item.price * (item.quantity || 1) : undefined,
        items: item ? [item] : undefined,
        productVariantId: d.productVariantId,
        source: d.source || 'product-form-success'
      }));
    });
    document.addEventListener('click', function (evt) {
      var checkout = evt.target.closest && evt.target.closest('button[name="checkout"], input[name="checkout"], a[href*="/checkout"], button[data-testid*="checkout" i], .cart__checkout-button');
      if (checkout) {
        getCart().then(function (cart) {
          trackStandard('begin_checkout', compact({
            currency: cart && cart.currency || (window.PWW_ANALYTICS_CONFIG || {}).currency || 'CAD',
            value: cart ? moneyToNumber(cart.total_price) : undefined,
            items: cart ? ga4ItemsFromCart(cart) : undefined,
            cart_item_count: cart && cart.item_count
          }));
        });
      }
      var remove = evt.target.closest && evt.target.closest('cart-remove-button, a[href*="/cart/change"], a[href*="quantity=0"]');
      if (remove) {
        trackCustom('remove_from_cart_click', { link_text: remove.textContent, href: remove.href });
      }
    }, true);
    document.addEventListener('change', function (evt) {
      var qty = evt.target.closest && evt.target.closest('input[name="updates[]"], input.quantity__input, quantity-input input');
      if (qty) trackCustom('cart_quantity_change', { quantity: qty.value, input_name: qty.name });
    }, true);
  }
  function initSearchAndFilterTracking() {
    document.addEventListener('submit', function (evt) {
      var form = evt.target;
      if (!form || !form.querySelector) return;
      var q = form.querySelector('input[name="q"], input[type="search"]');
      if (q) trackCustom('search_used', { search_term: q.value, form_action: form.action });
      if (form.matches && (form.matches('form[action*="/contact"]') || form.querySelector('[name="form_type"][value="contact"]'))) {
        trackCustom('contact_form_submit', { form_action: form.action });
      }
    }, true);
    if (location.pathname.indexOf('/search') === 0) {
      trackCustom('search_results_viewed', { search_term: params().get('q'), result_page: true });
    }
    document.addEventListener('change', function (evt) {
      var el = evt.target;
      if (!el) return;
      if (el.name === 'sort_by' || el.id === 'SortBy') trackCustom('sort_used', { sort_by: el.value });
      if ((el.name || '').indexOf('filter.') === 0 || el.closest && el.closest('facet-filters-form, .facets, .mobile-facets')) {
        trackCustom('filter_used', { filter_name: el.name, filter_value: el.value, checked: el.checked });
      }
    }, true);
    document.addEventListener('click', function (evt) {
      var link = evt.target.closest && evt.target.closest('a[href], button');
      if (!link) return;
      var href = link.href || '';
      var text = (link.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 120);
      if (href.indexOf('tel:') === 0) trackCustom('phone_click', { phone: href.replace('tel:', ''), link_text: text });
      if (href.indexOf('mailto:') === 0) trackCustom('email_click', { email: href.replace('mailto:', ''), link_text: text });
      if (/google\.com\/maps|maps\.app\.goo\.gl|\/pages\/contact|directions/i.test(href + ' ' + text)) trackCustom('directions_or_location_click', { href: href, link_text: text });
      if (/affirm|finance|financing|mpi/i.test(href + ' ' + text)) trackCustom('finance_click', { href: href, link_text: text });
      if (/logo|site-header__logo|header__heading-link/.test(link.className || '') || (link.closest && link.closest('.header__heading'))) trackCustom('logo_click', { href: href });
      if (link.matches('[data-pww-carousel-prev], [data-pww-carousel-next]')) trackCustom('carousel_click', { direction: link.matches('[data-pww-carousel-prev]') ? 'prev' : 'next' });
    }, true);
  }
  function initPwwSearchTracking() {
    document.addEventListener('click', function (evt) {
      var wheelBtn = evt.target.closest && evt.target.closest('[data-pww-wheel-search]');
      var tireBtn = evt.target.closest && evt.target.closest('[data-pww-tire-search]');
      if (!wheelBtn && !tireBtn) return;
      var box = (wheelBtn || tireBtn).closest('[data-pww-collection-search]') || document;
      if (wheelBtn) {
        trackCustom('wheel_size_selected', {
          wheel_diameter: (box.querySelector('[data-pww-wheel-diameter]') || {}).value,
          wheel_bolt_pattern: (box.querySelector('[data-pww-wheel-bolt]') || {}).value
        });
      }
      if (tireBtn) {
        trackCustom('tire_size_selected', {
          tire_rim_diameter: (box.querySelector('[data-pww-tire-diameter]') || {}).value,
          tire_width: (box.querySelector('[data-pww-tire-width]') || {}).value
        });
      }
    }, true);
    var vc = getVehicleContext();
    if (vc.pww_vehicle) trackCustom('vehicle_context_loaded', vc);
  }
  function initProductEngagementTracking() {
    if ((window.PWW_ANALYTICS_CONFIG || {}).page_type !== 'product') return;
    document.addEventListener('change', function (evt) {
      var el = evt.target;
      if (el && (el.matches('select[name="id"], input[name="id"], variant-selects select, variant-radios input') || (el.closest && el.closest('variant-selects, variant-radios')))) {
        trackCustom('variant_selected', { value: el.value, name: el.name });
      }
    }, true);
    document.addEventListener('click', function (evt) {
      if (evt.target.closest && evt.target.closest('.product__media, .product__media-toggle, product-modal-opener, .thumbnail')) {
        trackCustom('product_image_click', productContextFromWindow());
      }
      if (evt.target.closest && evt.target.closest('.share-button, share-button')) trackCustom('share_product', productContextFromWindow());
    }, true);
  }
  function exposeApi() {
    window.PWW = window.PWW || {};
    window.PWW.analytics = { version: VERSION, track: send, trackCustom: trackCustom, trackStandard: trackStandard, getCart: getCart };
    window.PWW.track = function (eventName, payload) { return send(eventName, payload || {}); };
  }
  function init() {
    exposeApi();
    initCollectionTracking();
    initCartTracking();
    initSearchAndFilterTracking();
    initPwwSearchTracking();
    initProductEngagementTracking();
    log('ready', window.PWW_ANALYTICS_CONFIG || {});
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
